import { useContext } from "react";

import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../../../common/components/Spinner/Spinner";
import { selectTargetSubtotal } from "../../../redux/activeQuoteSelectors";
import PackageEntryList from "./PackageEntryList";
import { ActiveQuoteShowInternalContext } from "../ActiveQuoteEdit";
import { selectSubtotal, selectTotalQty } from "~/feature/cart/cart/redux/selectors/productEntrySelectors";
import { type RootState } from "../../../../redux/store";
import { IconType } from "../../../../common/components/icon/IconType";
import { copyLastPackageEntry } from "~/feature/cart/cart/redux/actions/cartPageActions";
import CartEntryProductInfo from "~/feature/cart/cart/components/CartPage/CartEntryProductInfo";
import { isConfiguratorProduct } from "../../../../models/ProductData";
import {
  DeliverySubtotal,
  PriceSubtotal,
  QuantitySubtotal,
  TargetPriceSubtotal,
} from "~/feature/cart/cart/components/commonParts";
import { useTranslation } from "react-i18next";
import type { GroupedCartEntry } from "~/feature/cart/cart/models/CartData";
import {
  compareRGCEEquality,
  isProductEntryFcc,
  selectResolvedGroupedEntry,
} from "~/feature/cart/cart/components/CartPage/ProductTable/ProductEntryList/productEntryHelper";
import { Button } from "~/common/components/Button/Button";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";

interface Props {
  groupingEntry: GroupedCartEntry;
  className?: string;
  editMode?: boolean;
  index: number;
}

const ProductEntry = (props: Props) => {
  const resolvedEntry = useSelector(selectResolvedGroupedEntry(props.groupingEntry), compareRGCEEquality);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const copyEntry = () => {
    const group = resolvedEntry.packGroups[0];
    return dispatch(copyLastPackageEntry(group.packEntryIds[group.packEntryIds.length - 1]));
  };
  const cartEntry = resolvedEntry.cartEntry;
  const product = resolvedEntry.descriptors[0];

  const totalQuantity = useSelector((state: RootState) => selectTotalQty(state, { productCode: product.base.code }));
  const subtotal = useSelector((state: RootState) =>
    selectSubtotal(state, { productCode: product.base.code, isInCheckout: false }),
  );
  const targetSubtotal = useSelector((state: RootState) => selectTargetSubtotal(state, product.base.code));

  const { className, editMode } = props;

  const cls = classNames(className, "scha__cart__product-entry qproductentry_" + props.index);
  const showInternal = useContext(ActiveQuoteShowInternalContext);

  const base = product.base;
  const productIsLinear = isConfiguratorProduct(base);

  const disableWS = window.SCHA.config.disableAllWS;

  const isFcc = isProductEntryFcc(resolvedEntry);

  return (
    <div className={cls}>
      <div className="scha__cart__product-table__left-side">
        <CartEntryProductInfo productDescriptor={product} isFcc={isFcc} />
      </div>

      <div className="scha__active-quote__product-table__right-side">
        {product.isLoading && <Spinner className={"my-2"} />}

        <PackageEntryList editMode={editMode} packEntryIds={cartEntry.packEntryIds} />

        {editMode && base.quantityVisible && (
          <div className="mt-3">
            <div style={{ marginLeft: "-22px" }} className={"scha__cart__product-table__right-side__summary-container"}>
              {!disableWS && editMode && !productIsLinear && (
                <div className={"pl-3"}>
                  <Button
                    size={"medium"}
                    variant={"text"}
                    data-test-id={"btn-add-row"}
                    startIcon={<DefaultButtonIcon type={IconType.Plus} />}
                    onClick={copyEntry}
                  >
                    {t("cartPage.table.copyRow")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="scha__active-quote__product-table__right-side__summary-container pt-30 p-relative">
          <QuantitySubtotal className={"pl-3 pr-2"} quantity={totalQuantity} />
          <DeliverySubtotal
            className={"pl-2 pr-2"}
            quantity={cartEntry.packEntryIds.length}
            isSubscription={!base.quantityVisible}
          />
          <PriceSubtotal className={`pl-2 pr-${!showInternal || !editMode ? 3 : 2}`} price={subtotal} />

          {showInternal && editMode && (
            <>
              <div className="scha__active-quote__summary-internal">
                <div className="scha__active-quote__summary-internal__bg" />
              </div>
              <div className="scha__active-quote__summary-internal">
                <div className="scha__active-quote__summary-internal__bg" />
                <TargetPriceSubtotal className={"pl-2 pr-3"} price={targetSubtotal} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductEntry;
